import type { ExchangeAccountOption } from '@peanuds/exchange';
import type { UserAccount } from '@peanuds/shared-state';

/**
 * Currencies supported by the IBANs UI.
 * The SDK uses `string` for currency; this narrows it to the set the frontend supports.
 */
export type IbanCurrencyCode = 'EUR' | 'USD' | 'GBP';

export const SUPPORTED_IBAN_CURRENCIES: readonly IbanCurrencyCode[] = ['EUR', 'USD', 'GBP'];

const IBAN_CURRENCY_CODES: ReadonlySet<string> = new Set<string>(SUPPORTED_IBAN_CURRENCIES);

export const isIbanCurrencyCode = (value: string): value is IbanCurrencyCode =>
  IBAN_CURRENCY_CODES.has(value);

/** Narrows ExchangeAccountOption to the currencies and fields the IBANs UI requires. */
export type AccountOption = ExchangeAccountOption & { currency: IbanCurrencyCode; iban: string };

/**
 * View-model for account cards/tables. Maps from the raw SDK account shape
 * (alias, availableBalance, etc.) to a clean presentation contract.
 */
export type IbanAccount = {
  id: string;
  label: string;
  iban: string;
  recipient: string | null;
  currency: IbanCurrencyCode;
  balance: number;
  isPending: boolean;
  status: UserAccount['status'];
};

type IbanSourceAccount = Pick<
  UserAccount,
  | 'id'
  | 'alias'
  | 'iban'
  | 'accountHolderName'
  | 'currency'
  | 'availableBalance'
  | 'mainAccountId'
  | 'status'
>;

export const getIbanAccountLabel = (account: IbanSourceAccount, fallbackLabel = 'Account') => {
  const alias = typeof account.alias === 'string' ? account.alias.trim() : '';
  return alias || fallbackLabel;
};

export const mapToIbanAccount = (account: IbanSourceAccount): IbanAccount | null => {
  if (!isIbanCurrencyCode(account.currency)) {
    return null;
  }

  return {
    id: account.id,
    label: getIbanAccountLabel(account),
    iban: typeof account.iban === 'string' ? account.iban : '',
    recipient: typeof account.accountHolderName === 'string' ? account.accountHolderName : null,
    currency: account.currency,
    balance: account.availableBalance,
    isPending: account.status === 'pending',
    status: account.status,
  };
};
